import "./index.css";
import {
  FloatingFocusManager,
  FloatingOverlay,
  FloatingPortal,
  useDismiss,
  useFloating,
  useInteractions,
} from "@floating-ui/react";
import { useCallback, useEffect, useState } from "react";
import { clsx } from "clsx";
import { DrawerContent } from "./DrawerContent";
import { DrawerContext } from "./context";
import type { IDrawerProps } from "./types";

export const Drawer = (props: IDrawerProps) => {
  const { open, onOpenChange } = props;
  const [mounted, setMounted] = useState(false);
  const [visible, setVisible] = useState(false);

  const { context: floatingContext } = useFloating({
    open,
    onOpenChange,
  });

  const dismiss = useDismiss(floatingContext, {
    enabled: !props.preventClose,
    escapeKey: !props.preventClose,
    outsidePress: props.preventClose ? false : props.closeOnOutsidePress,
    outsidePressEvent: 'mousedown',
  });

  const { getFloatingProps } = useInteractions([dismiss]);

  const handleDismiss = useCallback(() => {
    onOpenChange(false);
  }, [onOpenChange]);

  const handleAnimationEnd = useCallback(() => {
    if (!open) {
      setMounted(false);
    }
  }, [open]);

  useEffect(() => {
    if (open) {
      setMounted(true);
      const timer = setTimeout(() => setVisible(true), 10);
      return () => clearTimeout(timer);
    } else {
      setVisible(false);
    }
  }, [open]);

  const contextValue = {
    ...props,
    handleDismiss,
    floatingContext,
    getFloatingProps,
    handleAnimationEnd,
    mounted,
    visible,
  };

  return (
    <DrawerContext.Provider value={contextValue}>
      <FloatingPortal>
        {mounted && (
          <>
            <FloatingOverlay
              className={clsx(
                "nuco-drawer__overlay",
                visible
                  ? "nuco-drawer__overlay--visible"
                  : "nuco-drawer__overlay--hidden",
              )}
              lockScroll
            />
            <FloatingFocusManager
              context={floatingContext}
              returnFocus={true}
              initialFocus={0}
              modal={true}
            >
              <DrawerContent />
            </FloatingFocusManager>
          </>
        )}
      </FloatingPortal>
    </DrawerContext.Provider>
  );
};
